import { Field, Form, Formik } from 'formik'
import React, { useCallback, useMemo } from 'react'
import * as Yup from 'yup'
import { Blog, BlogFormProps, BlogSubmitResponse, SubmitHandler } from './types'
import { TrixEditor } from 'src/modules/trix_editor'
import { CtaEditor, NewsletterRecipientsSelection } from 'src/orga_manage'
import { LangCounts } from 'src/orga_manage/newsletter_recipients_selection/types'
import { FormGroup, I18nHtml, JSONFetcher, postValues } from 'src/shared'
import { Button } from 'src/shared/components/button'

export const BlogForm = ({
  subscriberCounts,
  blog: blog_,
  formUrl,
  backUrl,
  ctaOptions,
  multilanguage,
  apiBaseUrl,
  imagesUrl,
  imageUploadUrl,
}: BlogFormProps): JSX.Element => {
  const blog = useMemo(() => JSON.parse(blog_) as Blog, [blog_])
  const langCounts = useMemo(() => JSON.parse(subscriberCounts) as LangCounts, [subscriberCounts])
  const newRecord = useMemo(() => !blog.id, [blog])
  // ctaOptions are empty for fundraising_events; they are only parsed deep within CtaEditor
  const showCallToActionEditor = useMemo(() => Object.keys(JSON.parse(ctaOptions)).length !== 0, [ctaOptions])

  const initialValues = {
    lang: blog.lang || '',
    title: blog.title || '',
    body: blog.body || '',
    // Toggles the Yup validation and represents the CtaEditor checkbox that toggles the cta form.
    _has_call_to_action: showCallToActionEditor && (newRecord || blog.call_to_action_headline !== ''),
    call_to_action_headline: blog.call_to_action_headline || '',
    call_to_action_button: blog.call_to_action_button || '',
    call_to_action_url: blog.call_to_action_url || '',
    _call_to_action_preselection: blog.call_to_action_headline ? 'manual-0' : '',
    // Used deep within CtaEditor via Formik
    _interval: 'single',
    _amount: 50,
    _owner_id: blog.owner_id || 0,
  }

  const submitHandler: SubmitHandler<typeof initialValues> = useCallback(
    (values, formikBag) => {
      const { lang: lang_, ...blogValues } = postValues(values)

      const lang = lang_ === '' ? null : lang_
      JSONFetcher.submit({
        method: newRecord ? 'post' : 'put',
        url: formUrl,
        body: { blog: { ...blogValues, lang } },
        success: (response: BlogSubmitResponse) => {
          if (response.redirect_url) {
            window.location.href = response.redirect_url
          } else {
            Object.entries(response.errors).forEach(([field, message]) => {
              formikBag.setFieldError(field, message)
            })
            formikBag.setSubmitting(false)
          }
        },
      })
    },
    [newRecord, formUrl]
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        title: Yup.string()
          .required(i18n.t('manage.blog_form.errors.blank'))
          .max(255, i18n.t('manage.blog_form.errors.too_long')),
        body: Yup.string().required(i18n.t('manage.blog_form.errors.blank')),
        _call_to_action_preselection: Yup.string().when('_has_call_to_action', {
          is: true,
          then: () => Yup.string().required(i18n.t('manage.blog_form.errors.blank')),
        }),
        call_to_action_headline: Yup.string().when('_has_call_to_action', {
          is: true,
          then: () =>
            Yup.string()
              .required(i18n.t('manage.blog_form.errors.blank'))
              .max(255, i18n.t('manage.blog_form.errors.too_long')),
        }),
        call_to_action_button: Yup.string().when('_has_call_to_action', {
          is: true,
          then: () =>
            Yup.string()
              .required(i18n.t('manage.blog_form.errors.blank'))
              .max(255, i18n.t('manage.blog_form.errors.too_long')),
        }),
        call_to_action_url: Yup.string().when('_has_call_to_action', {
          is: true,
          then: () =>
            Yup.string()
              .required(i18n.t('manage.blog_form.errors.blank'))
              .max(255, i18n.t('manage.blog_form.errors.too_long')),
        }),
      }),
    []
  )

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize={true}
      onSubmit={submitHandler}
    >
      {(formikProps) => (
        <Form noValidate>
          {multilanguage === 'true' && (
            <>
              <h3>{i18n.t('manage.blog_form.headline_subscription_filter_html')}</h3>
              <p>{i18n.t('manage.blog_form.description_subscription_filter_html')}</p>
              <NewsletterRecipientsSelection apiBaseUrl={apiBaseUrl} langCounts={langCounts} />
            </>
          )}
          <h3>{i18n.t('manage.blog_form.headline_content_html')}</h3>
          <Field
            name="title"
            maxLength={255}
            label={i18n.t('manage.blog_form.subject_label')}
            component={FormGroup}
            required
          />
          <Field type="hidden" name="body" label={i18n.t('manage.blog_form.body_label')} component={FormGroup} required>
            <TrixEditor
              imagesUrl={imagesUrl}
              imageUploadUrl={imageUploadUrl}
              showVideoButton={true}
              onChange={(value) => void formikProps.setFieldValue('body', value)}
              value={formikProps.values.body}
            />
          </Field>
          {showCallToActionEditor && <CtaEditor model="blog" options={ctaOptions} />}
          <div className="generic-box-actions">
            <Button type="submit" className="btn btn-primary btn-large" loading={formikProps.isSubmitting}>
              {i18n.t('verbs.preview')}
            </Button>
            <I18nHtml i18nKey="manage.blog_form.publish_next_step_html" /> <span>{i18n.t('misc.or')}</span>{' '}
            <a href={backUrl} className="hover:text-red-700">
              {i18n.t('verbs.cancel')}
            </a>
          </div>
        </Form>
      )}
    </Formik>
  )
}
